import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormControl, Validators, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { PermissionAction } from '../../../shared/enums/permission-action.enum';
import { GroupOption } from '../../../shared/models/group.model';
import { Project } from '../../../shared/models/project.model';
import { ProjectsService } from '../../../projects/projects.service';

export interface PickerProject {
  _id: string,
  label: string,
  groupLabel: string,
  groupId: string,
}

interface ProjectsByIdMap {
  [key: string]: PickerProject,
}

export const emptyPickerProject: PickerProject = {
  _id: '',
  label: '',
  groupLabel: '',
  groupId: '',
};

@Component({
  host: {
    'class': 'project-picker',
    'test-id': 'project-picker',
  },
  selector: 'project-picker',
  templateUrl: './project-picker.component.html',
})
export class ProjectPickerComponent implements OnChanges {
  @Input() filter: (value: Project, index?: number, array?: Project[]) => boolean
  @Input() placeholder: string
  @Input() permissionActions: PermissionAction[] = []
  @Input() useSolidBackground = true
  @Input() useMultiSelect = false
  @Input() validators: ValidatorFn[] = []
  @Input() value: PickerProject
  @Input() multiValue: PickerProject[]

  @Output() valueChange = new EventEmitter<PickerProject>()
  @Output() multiValueChange = new EventEmitter<PickerProject[]>()

  public emptyProject = emptyPickerProject
  public emptyProjectLabel = ''
  public groupedProjects$: Observable<GroupOption[]>
  public projectPicker: FormControl
  public projectsById: ProjectsByIdMap = {}

  private subscriptions = {
    groupedProjects: Subscription.EMPTY,
    messages: Subscription.EMPTY,
  }

  get errors(): ValidationErrors {
    return this.projectPicker?.errors;
  }

  get touched(): boolean {
    return this.projectPicker?.touched;
  }

  get valid(): boolean {
    return this.projectPicker?.valid;
  }

  constructor(
    private projectsService: ProjectsService,
  ) {}

  ngOnInit(): void {
    this.projectsById[emptyPickerProject._id] = emptyPickerProject;
    this.projectPicker = new FormControl(
      (this.useMultiSelect
        ? this.value ?? []
        : this.value ? (this.value as PickerProject)._id : emptyPickerProject._id
      ),
      Validators.compose(this.validators),
    );
    this.groupedProjects$ = this.projectsService.getGroupedProjects([
      PermissionAction.ContainersViewMetadata,
      ...this.permissionActions,
    ]).pipe(
      map(groups => {
        if (!this.filter) {
          return groups;
        }
        const filteredGroups = [];
        for (const group of groups) {
          const filteredProjects = group.projects.filter(this.filter);
          if (filteredProjects.length > 0) {
            filteredGroups.push({ ...group, projects: filteredProjects });
          }
        }
        return filteredGroups;
      })
    );
    this.subscriptions.groupedProjects = this.groupedProjects$.subscribe(groups => {
      for (const group of groups) {
        for (const project of group.projects) {
          this.projectsById[project._id] = {
            _id: project._id,
            label: project.label,
            groupLabel: group.label,
            groupId: group._id,
          };
        }
      }
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    if (!this.projectPicker) {
      return;
    }
    if (!this.useMultiSelect && this.value?._id !== this.projectPicker.value ) {
      this.projectPicker.setValue(this.value?._id);
    } else if (this.useMultiSelect && this.multiValue) {
      this.projectPicker.setValue((this.multiValue).map((project) => project._id ));
    }
  }

  setErrors(key: string, value: boolean): void {
    this.projectPicker.setErrors({ ...this.projectPicker.errors, [key]: value });
  }

  getGroupLabel(group): string {
    return `${group.label} ${group._id ? '(' + group._id + ')': ''}`;
  }

  selectProject(): void {
    if (this.useMultiSelect) {
      this.multiValue = this.projectPicker.value.map((id) => this.projectsById[id]);
      this.multiValueChange.emit(this.multiValue);
    }
    else {
      this.value = this.projectsById[this.projectPicker.value || ''];
      this.valueChange.emit(this.value);
    }
  }
}
